// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * UpgradesContext - Centralized Upgrades State Management
 * Replaces global UpgradesManager singleton with React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import GameConfig from '../config/GameConfig';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';
import { recordUpgrade } from '../utils/game/PermanentProgressManager';

const UpgradesContext = createContext(null);

export function UpgradesProvider({ children }) {
  // Initialize from localStorage or defaults
  const [purchasedUpgrades, setPurchasedUpgrades] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PURCHASED_UPGRADES);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved upgrades:', e);
      }
    }
    return [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PURCHASED_UPGRADES, JSON.stringify(purchasedUpgrades));
  }, [purchasedUpgrades]);

  // Get all available upgrades for a location
  const getAvailableUpgrades = useCallback((locationType) => {
    const allUpgrades = [];
    
    // Collect all upgrades from all categories
    const upgradeCategories = GameConfig.upgrades;
    Object.keys(upgradeCategories).forEach(category => {
      if (Array.isArray(upgradeCategories[category])) {
        upgradeCategories[category].forEach(upgrade => {
          allUpgrades.push({
            ...upgrade,
            category: category // Add category for filtering
          });
        });
      }
    });
    
    // Filter by location availability
    const availability = GameConfig.upgrades.availability[locationType];
    if (!availability) return [];
    
    if (availability === 'all') {
      return allUpgrades;
    }
    
    // Filter to only available upgrade IDs
    return allUpgrades.filter(upgrade => availability.includes(upgrade.id));
  }, []);

  // Check if upgrade is purchased
  const hasUpgrade = useCallback((upgradeId) => {
    return purchasedUpgrades.includes(upgradeId);
  }, [purchasedUpgrades]);

  // Get all purchased upgrades with full details
  const getPurchasedUpgrades = useCallback(() => {
    const upgrades = [];
    const upgradeCategories = GameConfig.upgrades;
    
    purchasedUpgrades.forEach(upgradeId => {
      // Find upgrade from all categories
      for (const category of Object.keys(upgradeCategories)) {
        if (Array.isArray(upgradeCategories[category])) {
          const upgrade = upgradeCategories[category].find(u => u.id === upgradeId);
          if (upgrade) {
            upgrades.push({
              ...upgrade,
              category: category
            });
            break;
          }
        }
      }
    });
    
    return upgrades;
  }, [purchasedUpgrades]);

  // Check if can purchase upgrade
  const canPurchase = useCallback((upgradeId) => {
    // Already purchased?
    if (purchasedUpgrades.includes(upgradeId)) {
      return false;
    }

    // Find upgrade definition from all categories
    let upgrade = null;
    const upgradeCategories = GameConfig.upgrades;
    for (const category of Object.keys(upgradeCategories)) {
      if (Array.isArray(upgradeCategories[category])) {
        upgrade = upgradeCategories[category].find(u => u.id === upgradeId);
        if (upgrade) break;
      }
    }
    
    if (!upgrade) {
      return false;
    }

    // Check prerequisites
    if (upgrade.requires && !purchasedUpgrades.includes(upgrade.requires)) {
      return false;
    }

    return true;
  }, [purchasedUpgrades]);

  // Purchase upgrade
  const purchaseUpgrade = useCallback((upgradeId) => {
    // Find upgrade definition from all categories
    let upgrade = null;
    const upgradeCategories = GameConfig.upgrades;
    for (const category of Object.keys(upgradeCategories)) {
      if (Array.isArray(upgradeCategories[category])) {
        upgrade = upgradeCategories[category].find(u => u.id === upgradeId);
        if (upgrade) break;
      }
    }
    
    if (!upgrade) {
      return { success: false, error: 'Upgrade not found' };
    }

    // Check if already purchased
    if (purchasedUpgrades.includes(upgradeId)) {
      return { success: false, error: 'Already purchased' };
    }

    // Check prerequisites
    if (upgrade.requires && !purchasedUpgrades.includes(upgrade.requires)) {
      return { success: false, error: 'Prerequisites not met' };
    }

    // Add to purchased list
    setPurchasedUpgrades(prev => [...prev, upgradeId]);

    // Record permanent mastery progress
    const idPrefix = upgradeId.split('_')[0]; // shield, hull, engine, sensor, fuel, auto, emergency
    const categoryMap = { shield: 'shields', hull: 'hull', engine: 'engines', sensor: 'sensors', fuel: 'special', auto: 'special', emergency: 'special' };
    const masteryCategory = categoryMap[idPrefix];
    if (masteryCategory && upgrade.tier) {
      recordUpgrade(masteryCategory, upgrade.tier);
    }

    return { 
      success: true, 
      upgrade
    };
  }, [purchasedUpgrades]);

  // Get upgrade tier (1, 2, or 3) for a category
  const getUpgradeTier = useCallback((category) => {
    // Check tier 3 first, then 2, then 1
    for (let tier = 3; tier >= 1; tier--) {
      const upgradeId = `${category}_${tier}`;
      if (purchasedUpgrades.includes(upgradeId)) {
        return tier;
      }
    }
    return 0; // No upgrade in this category
  }, [purchasedUpgrades]);

  // Get total bonus for a stat type
  const getTotalBonus = useCallback((statType) => {
    let totalBonus = 0;
    
    purchasedUpgrades.forEach(upgradeId => {
      // Find upgrade from all categories
      let upgrade = null;
      const upgradeCategories = GameConfig.upgrades;
      for (const category of Object.keys(upgradeCategories)) {
        if (Array.isArray(upgradeCategories[category])) {
          upgrade = upgradeCategories[category].find(u => u.id === upgradeId);
          if (upgrade) break;
        }
      }
      
      if (upgrade && upgrade[statType]) {
        totalBonus += upgrade[statType];
      }
    });

    return totalBonus;
  }, [purchasedUpgrades]);

  // Get fuel efficiency bonus (as decimal percentage, e.g., 0.15 for 15% reduction)
  const getFuelEfficiencyBonus = useCallback(() => {
    const bonus = getTotalBonus('fuelEfficiency');
    return bonus / 100; // Convert percentage to decimal (e.g., 15% -> 0.15)
  }, [getTotalBonus]);

  // Get scan range bonus
  const getScanRangeBonus = useCallback(() => {
    return getTotalBonus('scanRange');
  }, [getTotalBonus]);

  // Get shield strength bonus
  const getShieldBonus = useCallback(() => {
    return getTotalBonus('shieldStrength');
  }, [getTotalBonus]);

  // Get weapon damage bonus
  const getWeaponDamageBonus = useCallback(() => {
    return getTotalBonus('weaponDamage');
  }, [getTotalBonus]);

  // Get power generation bonus
  const getPowerBonus = useCallback(() => {
    return getTotalBonus('powerGeneration');
  }, [getTotalBonus]);

  // Check if special upgrade is unlocked
  const hasSpecialUpgrade = useCallback((specialType) => {
    // Special upgrades: emergency_warp, auto_repair
    return purchasedUpgrades.some(id => id.includes(specialType));
  }, [purchasedUpgrades]);

  // Reset to defaults (for game reset)
  const resetUpgrades = useCallback(() => {
    setPurchasedUpgrades([]);
    localStorage.removeItem(STORAGE_KEYS.PURCHASED_UPGRADES);
  }, []);

  const value = {
    purchasedUpgrades,
    getAvailableUpgrades,
    hasUpgrade,
    getPurchasedUpgrades,
    canPurchase,
    purchaseUpgrade,
    getUpgradeTier,
    getTotalBonus,
    getFuelEfficiencyBonus,
    getScanRangeBonus,
    getShieldBonus,
    getWeaponDamageBonus,
    getPowerBonus,
    hasSpecialUpgrade,
    resetUpgrades,
  };

  return (
    <UpgradesContext.Provider value={value}>
      {children}
    </UpgradesContext.Provider>
  );
}

// Custom hook to use upgrades context
export function useUpgrades() {
  const context = useContext(UpgradesContext);
  if (!context) {
    throw new Error('useUpgrades must be used within an UpgradesProvider');
  }
  return context;
}

export default UpgradesContext;
